import React, { useState, useEffect, useMemo } from 'react';
import { notifyCartUpdated } from '../../utils/cartEvents';
import { useHistory, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  ChevronUp, 
  ChevronDown, 
  Truck, 
  MessageSquare, 
  Bell, 
  ShoppingCart, 
  Package, 
  Calendar, 
  DollarSign, 
  FileText, 
  RotateCcw, 
  AlertCircle, 
  RefreshCw,
  Filter,
  MapPin,
  BarChart3
} from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { orderService } from '../../services/orderService';
import UserLayout from './UserLayout';
import OrderStatusFlowModal from './OrderStatusFlowModal';
import { SkeletonRows } from '../shared/Skeleton';
import { showNotification, showError } from '../../utils/notificationHandler';
import { useOrderingEnabled } from '../../hooks/useOrderingEnabled';
import { format } from 'date-fns';

const ORDER_STATUSES = [
  'All',
  'Outstanding Only',
  'Awaiting Approval',
  'In Progress',
  'Complete',
  'On Hold',
  'Cancelled'
];

const UserRecentOrders = () => {
  const location = useLocation();
  const { enabled: orderingEnabled, message: orderingMessage } = useOrderingEnabled();
  const [orders, setOrders] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [loadingNotifications, setLoadingNotifications] = useState(true);
  // Dashboard links pre-filter this page:
  //   ?search=<order_number>  that order, across all statuses
  //   ?status=<filter>        one of ORDER_STATUSES
  const urlParams = new URLSearchParams(location.search);
  const initialSearch = urlParams.get('search') || '';
  const statusParam = urlParams.get('status');
  const initialStatus = ORDER_STATUSES.includes(statusParam)
    ? statusParam
    : (initialSearch ? 'All' : 'Outstanding Only');
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [selectedOrderNote, setSelectedOrderNote] = useState('');
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [outstandingCount, setOutstandingCount] = useState(0);
  const [showOrderStatusModal, setShowOrderStatusModal] = useState(false);
  const [selectedOrderForTracking, setSelectedOrderForTracking] = useState(null);
  const history = useHistory();

  useEffect(() => {
    fetchRecentOrders();
    fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const fetchRecentOrders = async () => {
    try {
      setLoadingOrders(true);
      
      const outstandingOnly = statusFilter === 'Outstanding Only';
      
      // Get combined orders using the new service
      const { orders: combinedOrders } = await orderService.getAllOrders({
        outstandingOnly,
        includeLineItems: true,
        limit: 100,
        orderBy: 'created_at',
        orderDirection: 'desc'
      });

      // Get outstanding summary for display
      const summary = await orderService.getOutstandingOrdersSummary();
      setOutstandingCount(summary.total_outstanding);

      setOrders(combinedOrders || []);
    } catch (error) {
      console.error('Error fetching recent orders:', error);
      showError(error, 'Failed to load recent orders. Please try again later.');
    } finally {
      setLoadingOrders(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      setLoadingNotifications(true);
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) throw new Error('No user found');

      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setNotifications(data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      showError(error, 'Failed to load notifications. Please try again later.');
    } finally {
      setLoadingNotifications(false);
    }
  };

  const toggleOrderExpansion = (orderId) => {
    const newExpandedOrders = new Set(expandedOrders);
    if (newExpandedOrders.has(orderId)) {
      newExpandedOrders.delete(orderId);
    } else {
      newExpandedOrders.add(orderId);
    }
    setExpandedOrders(newExpandedOrders);
  };

  const handleReorder = (order) => {
    if (!orderingEnabled) {
      showNotification(orderingMessage, 'error');
      return;
    }
    try {
      let cartItems = [];
      
      // Only handle portal orders now
      if (order.source === 'portal' && order.cart_items) {
        cartItems = order.cart_items;
      }
      
      if (cartItems.length > 0) {
        localStorage.setItem('cart', JSON.stringify(cartItems));
        notifyCartUpdated();
        showNotification('Items added to cart for reordering', 'success');
        history.push('/user/cart');
      } else {
        showNotification('No items available for reordering', 'warning');
      }
    } catch (error) {
      showError(error, 'Failed to add items to cart. Please try again.');
    }
  };

  const getStatusColor = (status) => {
    return orderService.getStatusColor(status);
  };

  const getDisplayStatus = (status) => {
    return orderService.getStatusDisplayName(status);
  };

  const isOutstandingOrder = (order) => {
    return orderService.isOrderOutstanding(order);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedOrders = useMemo(() => {
    let sortableOrders = [...orders];
    
    // Always prioritize outstanding orders first
    sortableOrders.sort((a, b) => {
      const aOutstanding = isOutstandingOrder(a);
      const bOutstanding = isOutstandingOrder(b);
      
      // Outstanding orders come first
      if (aOutstanding && !bOutstanding) return -1;
      if (!aOutstanding && bOutstanding) return 1;
      
      // Then sort by the selected criteria
      if (sortConfig.key) {
        let aValue = a[sortConfig.key] || a.created_at;
        let bValue = b[sortConfig.key] || b.created_at;

        // Handle date fields
        if (sortConfig.key.includes('date') || sortConfig.key === 'created_at') {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
      }
      return 0;
    });
    
    return sortableOrders;
  }, [orders, sortConfig]);

  const filteredOrders = useMemo(() => {
    return sortedOrders.filter(order => {
      // Search filter
      const matchesSearch = order.order_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (order.mhs_reference && order.mhs_reference.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (order.customer_po && order.customer_po.toLowerCase().includes(searchTerm.toLowerCase()));
      
      // Status filter
      let matchesStatus = true;
      if (statusFilter === 'Outstanding Only') {
        matchesStatus = isOutstandingOrder(order);
      } else if (statusFilter === 'Awaiting Approval') {
        matchesStatus = orderService.isAwaitingApproval(order);
      } else if (statusFilter !== 'All') {
        const normalizedStatus = getDisplayStatus(order.status);
        matchesStatus = normalizedStatus === statusFilter;
      }
      
      return matchesSearch && matchesStatus;
    });
  }, [sortedOrders, searchTerm, statusFilter]);

  const handleViewNote = (note) => {
    setSelectedOrderNote(note);
    setShowNoteModal(true);
  };

  const handleMarkNotificationsAsRead = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) throw new Error('No user found');

      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', user.id);

      if (error) throw error;

      // Refresh notifications
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notifications as read:', error);
      showError(error, 'Failed to mark notifications as read. Please try again.');
    }
  };

  const handleTrackOrder = (order) => {
    setSelectedOrderForTracking(order);
    setShowOrderStatusModal(true);
  };

  const unreadNotificationsCount = notifications.filter(n => !n.read).length;

  return (
    <UserLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Recent Orders</h1>
            <p className="text-gray-600 mt-1">Track and manage your portal order history</p>
          </div>
        </div>

        {loadingOrders ? (
          <SkeletonRows rows={6} />
        ) : (
          <>
            {/* Outstanding Orders Summary */}
            {outstandingCount > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                  <div>
                    <p className="text-sm font-semibold text-red-900">
                      {outstandingCount} Outstanding Orders Require Attention
                    </p>
                    <p className="text-xs text-red-700">
                      Orders shown below are prioritized by status - outstanding orders appear first
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Filters and Search */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="space-y-4">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
                  <div className="relative flex-1 max-w-md">
                    <input
                      type="text"
                      placeholder="Search by Order Number, PO, or Reference"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-4 py-2 pr-10 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                    />
                    <Search className="absolute right-3 top-2.5 text-gray-400" size={20} />
                  </div>
                  
                  <div className="flex flex-wrap gap-3">
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                    >
                      {ORDER_STATUSES.map((status) => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Orders List */}
            <div className="space-y-4">
              {filteredOrders.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                  <Package className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-gray-500 text-lg">No orders found matching your criteria</p>
                  <p className="text-gray-400 text-sm mt-2">Try adjusting your search filters</p>
                </div>
              ) : (
                filteredOrders.map((order) => {
                  const isExpanded = expandedOrders.has(order.id);
                  const orderTotal = order.order_total_inc_tax || (
                    order.cart_items ? order.cart_items.reduce((sum, item) => sum + (item.price * item.quantity), 0) : 0
                  );
                  const isOutstanding = isOutstandingOrder(order);
                  
                  return (
                    <motion.div
                      key={order.id}
                      layout
                      className={`bg-white rounded-lg shadow-sm border overflow-hidden ${
                        isOutstanding 
                          ? 'border-red-300 ring-2 ring-red-100' 
                          : 'border-gray-200'
                      }`}
                    >
                      {/* Order Summary Header */}
                      <div className="p-6">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                          <div className="flex-1">
                            <div className="flex flex-col lg:flex-row lg:items-center space-y-2 lg:space-y-0 lg:space-x-6">
                              <div>
                                <h3 className="text-lg font-semibold text-gray-900">
                                  {order.order_number}
                                </h3>
                                <p className="text-sm text-gray-500 flex items-center mt-1">
                                  <Calendar className="h-4 w-4 mr-1" />
                                  {format(new Date(order.created_at), 'PPp')}
                                </p>
                              </div>
                              
                              <div className="flex flex-wrap items-center gap-4">
                                <div className="flex items-center space-x-2">
                                  {isOutstanding && (
                                    <AlertCircle className="h-4 w-4 text-red-600" />
                                  )}
                                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(order.status || 'Pending')}`}>
                                    {getDisplayStatus(order.status) || 'Pending'}
                                  </span>
                                </div>
                                
                                <div className="text-sm text-gray-600 flex items-center">
                                  <Package className="h-4 w-4 mr-1" />
                                  {order.cart_items?.length || 0} items
                                </div>
                                
                                <div className="text-lg font-semibold text-gray-900 flex items-center">
                                  <DollarSign className="h-4 w-4 mr-1" />
                                  {orderService.formatCurrency(orderTotal)}
                                </div>
                              </div>
                            </div>
                            
                            {/* Additional Info Row */}
                            <div className="mt-3 flex flex-wrap gap-4 text-sm text-gray-600">
                              {order.customer_po && (
                                <span className="flex items-center">
                                  <FileText className="h-4 w-4 mr-1" />
                                  PO: {order.customer_po}
                                </span>
                              )}
                              {order.mhs_reference && (
                                <span className="flex items-center">
                                  <FileText className="h-4 w-4 mr-1" />
                                  MHS: {order.mhs_reference}
                                </span>
                              )}
                              {order.shipping_status && order.shipping_status !== 'Not Shipped' && (
                                <span className="flex items-center">
                                  <Truck className="h-4 w-4 mr-1" />
                                  {order.shipping_status}
                                </span>
                              )}
                              {order.connote && order.connote !== 'N/A' && (
                                <span className="flex items-center">
                                  ConNote: {order.connote}
                                </span>
                              )}
                              {order.carrier && (
                                <span className="flex items-center">
                                  <Truck className="h-4 w-4 mr-1" />
                                  {order.carrier}
                                </span>
                              )}
                              {order.required_date && (
                                <span className="flex items-center">
                                  <Calendar className="h-4 w-4 mr-1" />
                                  Required: {format(new Date(order.required_date), 'dd/MM/yyyy')}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Action Buttons */}
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => toggleOrderExpansion(order.id)}
                              className="flex items-center px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                            >
                              {isExpanded ? (
                                <>
                                  <ChevronUp className="h-4 w-4 mr-1" />
                                  Hide Details
                                </>
                              ) : (
                                <>
                                  <ChevronDown className="h-4 w-4 mr-1" />
                                  View Details
                                </>
                              )}
                            </button>
                            
                            {orderingEnabled && (
                              <button
                                onClick={() => handleReorder(order)}
                                className="flex items-center px-3 py-2 bg-brand text-brand-dark rounded-lg hover:bg-brand-hover transition-colors duration-200 font-medium"
                              >
                                <RotateCcw className="h-4 w-4 mr-1" />
                                Reorder
                              </button>
                            )}
                            
                            <button
                              onClick={() => handleTrackOrder(order)}
                              className="flex items-center px-3 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors duration-200"
                              title="View order status progression and timeline"
                            >
                              <BarChart3 className="h-4 w-4 mr-1" />
                              Track Order
                            </button>
                            
                            {order.shipping_status && order.shipping_status !== 'Not Shipped' && (
                              <button
                                onClick={() => showNotification(`Shipping Status: ${order.shipping_status}${order.connote ? `\nConNote: ${order.connote}` : ''}`, 'info')}
                                className="flex items-center px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200"
                              >
                                <Truck className="h-4 w-4 mr-1" />
                                Shipping
                              </button>
                            )}
                            
                            {order.notes && (
                              <button
                                onClick={() => handleViewNote(order.notes)}
                                className="flex items-center px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-200"
                              >
                                <MessageSquare className="h-4 w-4 mr-1" />
                                Note
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Expandable Order Details */}
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="border-t border-gray-200"
                          >
                            <div className="p-6 bg-gray-50">
                              <h4 className="text-lg font-semibold text-gray-900 mb-4">Order Items</h4>
                              
                              <div className="space-y-3">
                                {order.cart_items?.map((item, index) => (
                                  <div key={index} className="bg-white p-4 rounded-lg border border-gray-200">
                                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-2 lg:space-y-0">
                                      <div className="flex-1">
                                        <h5 className="font-semibold text-gray-900">{item.partNumber}</h5>
                                        <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                                        {item.comment && (
                                          <p className="text-sm text-blue-600 mt-1 italic">
                                            Note: {item.comment}
                                          </p>
                                        )}
                                      </div>
                                      
                                      <div className="flex items-center space-x-4 text-sm">
                                        <span className="text-gray-600">
                                          Qty: <span className="font-medium">{item.quantity}</span>
                                        </span>
                                        <span className="text-gray-600">
                                          Unit: <span className="font-medium">{orderService.formatCurrency(parseFloat(item.price))}</span>
                                        </span>
                                        <span className="text-gray-900 font-semibold">
                                          Total: {orderService.formatCurrency(item.price * item.quantity)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                              
                              {/* Delivery Address Section */}
                              {order.delivery_addresses && (
                                <div className="mt-6 pt-4 border-t border-gray-200">
                                  <h5 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                                    <MapPin className="h-5 w-5 text-gray-600 mr-2" />
                                    Delivery Address
                                  </h5>
                                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                                    <div className="text-sm text-gray-700 space-y-1">
                                      <p className="font-medium text-gray-900">{order.delivery_addresses.address_line1}</p>
                                      {order.delivery_addresses.address_line2 && (
                                        <p>{order.delivery_addresses.address_line2}</p>
                                      )}
                                      <p>
                                        {order.delivery_addresses.city}, {order.delivery_addresses.state} {order.delivery_addresses.postal_code}
                                      </p>
                                      <p className="text-gray-600">{order.delivery_addresses.country || 'Australia'}</p>
                                      {order.delivery_addresses.is_primary && (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mt-2">
                                          Primary Address
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}
                              
                              <div className="mt-6 pt-4 border-t border-gray-200">
                                <div className="flex justify-between items-center">
                                  <span className="text-lg font-semibold text-gray-900">Order Total:</span>
                                  <span className="text-xl font-bold text-gray-900">{orderService.formatCurrency(orderTotal)}</span>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })
              )}
            </div>
          </>
        )}

        {/* Note Modal */}
        {showNoteModal && (
          <div className="fixed z-50 inset-0 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 py-8">
              <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowNoteModal(false)}></div>
              <div className="bg-white rounded-lg shadow-xl w-full max-w-md relative">
                <div className="p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Order Note</h3>
                  <p className="text-sm text-gray-700">{selectedOrderNote}</p>
                </div>
                <div className="px-6 py-4 bg-gray-100 text-right">
                  <button
                    onClick={() => setShowNoteModal(false)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors duration-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notifications Modal */}
        {showNotificationsModal && (
          <div className="fixed z-50 inset-0 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 py-8">
              <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowNotificationsModal(false)}></div>
              <div className="bg-white rounded-lg shadow-xl w-full max-w-md relative">
                <div className="p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Notifications</h3>
                  {loadingNotifications ? (
                    <div className="text-center">Loading...</div>
                  ) : notifications.length === 0 ? (
                    <p className="text-sm text-gray-700">No notifications.</p>
                  ) : (
                    <ul className="space-y-2">
                      {notifications.map((notification) => (
                        <li key={notification.id} className={`p-2 rounded ${notification.read ? 'bg-gray-100' : 'bg-yellow-100'}`}>
                          <p className="text-sm text-gray-800">{notification.message}</p>
                          <p className="text-xs text-gray-500">{format(new Date(notification.created_at), 'PPpp')}</p>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="px-6 py-4 bg-gray-100 flex justify-between">
                  <button
                    onClick={() => {
                      setShowNotificationsModal(false);
                      handleMarkNotificationsAsRead();
                    }}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors duration-200"
                  >
                    Mark All as Read
                  </button>
                  <button
                    onClick={() => setShowNotificationsModal(false)}
                    className="bg-gray-300 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors duration-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* React Flow Order Status Modal */}
        <OrderStatusFlowModal
          isOpen={showOrderStatusModal}
          onClose={() => setShowOrderStatusModal(false)}
          order={selectedOrderForTracking}
        />
      </div>
    </UserLayout>
  );
};

export default UserRecentOrders;
